import { Member } from "@stytch/vanilla-js/b2b";
import {
  Home as HomeIcon,
  Bell as BellIcon,
  Activity as ActivityIcon,
  Wheat as GrainIcon,
  FlaskConical as FlaskConicalIcon,
  LineChart as LineChartIcon,
  Upload as UploadIcon,
  BarChartBig as BarChartBigIcon,
  Info as InfoIcon,
  Zap as ZapIcon,
  CandlestickChart as CandlestickChartIcon,
  Receipt as ReceiptIcon,
  FileSpreadsheet as FileSpreadsheetIcon,
  Building as BuildingIcon,
  ListStart as ListStartIcon,
  FileBarChart as FileBarChartIcon,
  MessageCircle as MessageCircleIcon,
  UserCog as UserCogIcon,
  PhoneCall,
  CalendarDays as CalendarArrowDownIcon,
  Brain,
  FileIcon,
  UmbrellaIcon,
  CakeIcon,
  VideoIcon,
  Truck as TruckIcon,
} from "lucide-react";
import { PossiblePackage, doesUserHavePackages } from "../lib/models/auth";
import { RoutePath } from "./routerPaths";

export { RoutePath };



interface PageDefinition {
  name: string;
  href: RoutePath;
  icon: React.ElementType;
  requiredPackages: PossiblePackage[];
  isProtected: boolean;
  description?: string;
  background?: string;
  excludeFromDashboard?: boolean;
  shouldStayOnUnAuthed?: boolean;
  group?: string;
  current?: boolean;
}

export function pagesList({
  currentPathname,
  member,
  filterByPackages,
}: {
  member?: Member | null;
  currentPathname?: string;
  filterByPackages: boolean;
}): PageDefinition[] {
  const filteredPages = [
    {
      name: "Jobs Dashboard",
      href: RoutePath.DeveloperJobsDashboard,
      icon: BarChartBigIcon,
      requiredPackages: [PossiblePackage.Developer],
      description: "Job Status Dashboard",
      background: "bg-blue-500",
      isProtected: true,
      group: "Developer",
    },
    {
      name: "Chat",
      href: RoutePath.Chat,
      icon: MessageCircleIcon,
      requiredPackages: [PossiblePackage.Chat],
      description: "Chat with customers",
      background: "bg-pink-500",
      isProtected: true,
      group: "Communication",
    },
    {
      name: "Privacy Policy",
      href: RoutePath.PrivacyPolicy,
      icon: BarChartBigIcon,
      requiredPackages: [],
      description: "Privacy Policy",
      background: "bg-blue-500",
      isProtected: false,
      shouldStayOnUnAuthed: true,
      excludeFromDashboard: true,
    },
    {
      name: "Developer",
      href: RoutePath.DeveloperDashboard,
      icon: BarChartBigIcon,
      requiredPackages: [PossiblePackage.Developer],
      description: "Private dashboard to do developer actions",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Developer",
    },
    {
      name: "Manage Permissions",
      href: RoutePath.ManagePermissions,
      icon: UserCogIcon,
      requiredPackages: [PossiblePackage.ManagePermission],
      description: "Manage Permissions",
      background: "bg-blue-500",
      isProtected: true,
      group: "Management",
    },
    {
      name: "Mobile Permissions",
      href: RoutePath.MobilePermissions,
      icon: UserCogIcon,
      requiredPackages: [PossiblePackage.MobilePermissions],
      description: "Manage permissions for clients",
      background: "bg-blue-500",
      isProtected: true,
      group: "Management",
    },
    {
      name: "Client Org",
      href: RoutePath.MobilePermissionsClient,
      icon: UserCogIcon,
      requiredPackages: [PossiblePackage.MobilePermissions],
      description: "Manage client organization permissions",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Management",
    },
    {
      name: "Freight Org",
      href: RoutePath.MobilePermissionsFreight,
      icon: UserCogIcon,
      requiredPackages: [PossiblePackage.MobilePermissions],
      description: "Manage freight organization permissions",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Management",
    },
    {
      name: "Mobile Permissions User Detail",
      href: RoutePath.MobilePermissionsUserDetail,
      icon: UserCogIcon,
      requiredPackages: [PossiblePackage.MobilePermissions],
      description: "User permission details",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Management",
    },
    {
      name: "Knowledge Center",
      href: RoutePath.KnowledgeTasks,
      icon: Brain,
      requiredPackages: [PossiblePackage.KnowledgeTasks],
      description: "",
      background: "bg-blue-500",
      isProtected: true,
      group: "General",
    },
    {
      name: "Checklist",
      href: RoutePath.TaskChecklists,
      icon: Brain,
      requiredPackages: [PossiblePackage.KnowledgeTasks],
      description: "",
      background: "bg-blue-500",
      isProtected: true,
      group: "General",
    },
    {
      name: "Manual Price Data",
      href: RoutePath.ManualPriceData,
      icon: BarChartBigIcon,
      requiredPackages: [PossiblePackage.PriceData],
      description: "Add manual futures contract price data",
      background: "bg-blue-500",
      excludeFromDashboard: true,
      isProtected: true,
      group: "Knowledge",
    },
    {
      name: "Training Task Detail",
      href: RoutePath.KnowledgeTaskDetail,
      icon: BarChartBigIcon,
      requiredPackages: [PossiblePackage.KnowledgeTasks],
      description: "Manage a training task",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Knowledge",
    },
    {
      name: "Training Task Edit",
      href: RoutePath.KnowledgeTaskEdit,
      icon: BarChartBigIcon,
      requiredPackages: [PossiblePackage.KnowledgeTasks],
      description: "Edit a training task",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Knowledge",
    },
    {
      name: "Create Training Task",
      href: RoutePath.KnowledgeTaskCreate,
      icon: BarChartBigIcon,
      requiredPackages: [PossiblePackage.KnowledgeTasks],
      description: "Create a new training task",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Knowledge",
    },
    {
      name: "Developer Test",
      href: RoutePath.DeveloperTestPage,
      icon: BarChartBigIcon,
      requiredPackages: [PossiblePackage.Developer],
      description: "Test new functionality",
      background: "bg-blue-500",
      isProtected: true,
      group: "Developer",
    },
    {
      name: "Developer Client Notifications",
      href: RoutePath.DeveloperClientNotifications,
      icon: BarChartBigIcon,
      requiredPackages: [
        PossiblePackage.Developer,
        PossiblePackage.ClientNotification,
      ],
      description: "Send client notifications",
      background: "bg-blue-500",
      isProtected: true,
      group: "Developer",
    },
    {
      name: "Authenticate",
      href: RoutePath.Authenticate,
      icon: BarChartBigIcon,
      requiredPackages: [],
      description: "Authenticate",
      background: "bg-blue-500",
      isProtected: false,
      excludeFromDashboard: true,
      group: "Developer",
    },
    {
      name: "Login",
      href: RoutePath.Login,
      icon: BarChartBigIcon,
      requiredPackages: [],
      description: "Login Page",
      background: "bg-blue-500",
      isProtected: false,
      excludeFromDashboard: true,
      group: "Developer",
    },
    {
      name: "Destination Consent",
      href: RoutePath.DestinationMessagingConsent,
      icon: MessageCircleIcon,
      requiredPackages: [],
      description: "",
      background: "bg-pink-500",
      isProtected: false,
      excludeFromDashboard: true,
      shouldStayOnUnAuthed: true,
      group: "Communication",
    },
    {
      name: "Destination Consent Screenshot",
      href: RoutePath.DestinationMessagingConsentScreenshot,
      icon: MessageCircleIcon,
      requiredPackages: [],
      description: "",
      background: "bg-pink-500",
      isProtected: false,
      excludeFromDashboard: true,
      shouldStayOnUnAuthed: true,
      group: "Communication",
    },
    {
      name: "Dashboard",
      href: RoutePath.Dashboard,
      icon: HomeIcon,
      requiredPackages: [],
      isProtected: true,
    },
    {
      name: "Analysis",
      href: RoutePath.HedgeBeaconAnalysis,
      icon: ActivityIcon,
      requiredPackages: [PossiblePackage.HedgeBeacon],
      description:
        "Write analysis for upcoming Daily HedgeBeacon Analysis emails.",
      background: "bg-indigo-500",
      isProtected: true,
      group: "Hedge Beacon",
    },
    {
      name: "Email Preview",
      href: RoutePath.HedgeBeaconEmailPreview,
      icon: ActivityIcon,
      requiredPackages: [PossiblePackage.HedgeBeacon],
      description: "Preview the email for a given set of details.",
      background: "bg-indigo-500",
      isProtected: true,
      group: "Hedge Beacon",
    },
    {
      name: "Alerts",
      href: RoutePath.HedgeBeaconAlert,
      icon: BellIcon,
      requiredPackages: [PossiblePackage.HedgeBeacon],
      description:
        "Create alerts on commodities and have them sent out as texts on publish.",
      background: "bg-indigo-500",
      isProtected: true,
      group: "Hedge Beacon",
    },
    {
      name: "Market Thoughts",
      href: RoutePath.HedgeBeaconMarketThoughts,
      icon: ZapIcon,
      requiredPackages: [PossiblePackage.HedgeBeaconMarketThoughts],
      description: "Send out market thoughts as texts on publish.",
      background: "bg-pink-500",
      isProtected: true,
      group: "Hedge Beacon",
    },
    {
          name: "Video",
          href: RoutePath.VideoDashboard,
          icon: VideoIcon,
          requiredPackages: [PossiblePackage.HedgeBeaconMarketThoughts],
          description: "Splice together videos",
          background: "bg-pink-500",
          isProtected: true,
      excludeFromDashboard: true,
          group: "Hedge Beacon",
      },
      {
      name: "Video",
      href: RoutePath.HedgeBeaconSplicing,
      icon: VideoIcon,
      requiredPackages: [PossiblePackage.HedgeBeaconMarketThoughts],
      description: "Upload and splice videos",
      background: "bg-pink-500",
      isProtected: true,
      group: "Hedge Beacon",
    },
    {
      name: "News Articles",
      href: RoutePath.NewsArticles,
      icon: MessageCircleIcon,
      requiredPackages: [PossiblePackage.News],
      description: "",
      background: "bg-pink-500",
      isProtected: true,
      excludeFromDashboard: false,
      group: "Trading",
    },
    {
      name: "Market Similarity",
      href: RoutePath.MarketSimilarity,
      icon: MessageCircleIcon,
      requiredPackages: [],
      description: "",
      background: "bg-pink-500",
      isProtected: true,
      excludeFromDashboard: false,
      group: "Trading",
    },
    {
      name: "Call Logs",
      href: RoutePath.CallLogsDashboard,
      icon: PhoneCall,
      requiredPackages: [PossiblePackage.CallLog],
      description: "",
      background: "bg-pink-500",
      isProtected: true,
      excludeFromDashboard: false,
      group: "Communication",
    },
    {
      name: "Ticket Jobs",
      href: RoutePath.TicketsJobsDashboard,
      icon: InfoIcon,
      requiredPackages: [PossiblePackage.Tickets],
      description: "View status of PDF ticket jobs",
      background: "bg-pink-500",
      isProtected: true,
      group: "Tickets",
    },
    {
      name: "Weight Tickets",
      href: RoutePath.TicketsWeightDashboard,
      icon: GrainIcon,
      requiredPackages: [PossiblePackage.Tickets],
      description: "View and download weight tickets by provider",
      background: "bg-pink-500",
      isProtected: true,
      group: "Tickets",
    },
    {
      name: "Trading Tickets",
      href: RoutePath.TicketsTradingDashboard,
      icon: LineChartIcon,
      requiredPackages: [PossiblePackage.Tickets],
      description: "View and download trading tickets by provider",
      background: "bg-pink-500",
      isProtected: true,
      group: "Futures Upload",
    },
    {
      name: "Staged Dorman Tickets",
      href: RoutePath.DormanStagedDashboard,
      icon: LineChartIcon,
      requiredPackages: [PossiblePackage.Tickets],
      description: "Pair Dorman and TT orders",
      background: "bg-pink-500",
      isProtected: true,
      group: "Futures Upload",
    },
    {
      name: "Mobile Contact Us",
      href: RoutePath.ContactsDashboard,
      icon: BuildingIcon,
      requiredPackages: [PossiblePackage.Employee],
      description: "",
      background: "bg-pink-500",
      isProtected: true,
      group: "Management",
    },
    {
      name: "Mobile Client Users",
      href: RoutePath.MobileClientUsers,
      icon: BuildingIcon,
      requiredPackages: [PossiblePackage.ClientManageUsers],
      description: "",
      background: "bg-pink-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Management",
    },
    {
      name: "Locations",
      href: RoutePath.LocationDashboard,
      icon: BuildingIcon,
      requiredPackages: [PossiblePackage.General],
      description: "",
      background: "bg-pink-500",
      isProtected: true,
      group: "Management",
    },
    {
      name: "Cost Basis",
      href: RoutePath.CostBasisDashboard,
      icon: BuildingIcon,
      requiredPackages: [PossiblePackage.CostBasis],
      description: "",
      background: "bg-pink-500",
      isProtected: true,
      group: "General",
    },
    {
      name: "Market Zones",
      href: RoutePath.MarketZoneDashboard,
      icon: BuildingIcon,
      requiredPackages: [PossiblePackage.General],
      description: "",
      background: "bg-pink-500",
      isProtected: true,
      group: "Management",
    },
    {
      name: "Destination Messaging",
      href: RoutePath.DestinationMessagingDashboard,
      icon: MessageCircleIcon,
      requiredPackages: [PossiblePackage.Destination],
      description: "",
      background: "bg-pink-500",
      isProtected: true,
      group: "Communication",
    },
    {
      name: "Contracts Entry",
      href: RoutePath.ContractsDashboard,
      icon: FileBarChartIcon,
      requiredPackages: [],
      description: "",
      background: "bg-blue-500",
      isProtected: true,
      group: "Contracts",
    },
    {
      name: "OTC Report",
      href: RoutePath.OtcDashboard,
      icon: FileBarChartIcon,
      requiredPackages: [PossiblePackage.OTC],
      description: "Contracts report",
      background: "bg-pink-500",
      isProtected: true,
      group: "Contracts",
    },
    {
      name: "General Contracts",
      href: RoutePath.GeneralContractsDashboard,
      icon: FileBarChartIcon,
      requiredPackages: [PossiblePackage.GeneralContract],
      description: "",
      background: "bg-pink-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Contracts",
    },
    {
      name: "General Contracts",
      href: RoutePath.ContractsGeneralDashboard,
      icon: FileBarChartIcon,
      requiredPackages: [PossiblePackage.GeneralContract],
      description: "",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Contracts",
    },
    {
      name: "OTC Contracts",
      href: RoutePath.ContractsOtcDashboard,
      icon: FileBarChartIcon,
      requiredPackages: [PossiblePackage.OTC],
      description: "",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Contracts",
    },
    {
      name: "General Contract Language",
      href: RoutePath.ContractsLanguageGeneralDashboard,
      icon: FileBarChartIcon,
      requiredPackages: [PossiblePackage.GeneralContract],
      description: "",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Contracts",
    },
    {
      name: "OTC Contract Language",
      href: RoutePath.ContractsLanguageOtcDashboard,
      icon: FileBarChartIcon,
      requiredPackages: [PossiblePackage.OTC],
      description: "",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Contracts",
    },
    {
      name: "General Contract's Settings",
      href: RoutePath.GeneralContractSettingsDashboard,
      icon: FileBarChartIcon,
      requiredPackages: [PossiblePackage.GeneralContract],
      description: "",
      background: "bg-pink-500",
      isProtected: true,
      group: "Contracts",
    },
    {
      name: "Staged General Contracts",
      href: RoutePath.StagedGeneralContractsDashboard,
      icon: FileBarChartIcon,
      requiredPackages: [PossiblePackage.GeneralContract],
      description: "",
      background: "bg-pink-500",
      isProtected: true,
      group: "Contracts",
    },
    {
      name: "Agtech Contracts",
      href: RoutePath.AgtechContractsDashboard,
      icon: FileBarChartIcon,
      requiredPackages: [PossiblePackage.GeneralContract, PossiblePackage.OTC],
      description: "",
      background: "bg-pink-500",
      isProtected: true,
      group: "Contracts",
    },
    {
      name: "Positions Report",
      href: RoutePath.PositionsDashboard,
      icon: FileBarChartIcon,
      requiredPackages: [PossiblePackage.OTC],
      description: "Positions report",
      background: "bg-pink-500",
      isProtected: true,
      group: "Reports",
    },
    {
      name: "Customers",
      href: RoutePath.ManageCustomers,
      icon: BuildingIcon,
      requiredPackages: [PossiblePackage.ManagerCustomer],
      description: "Manage Customers",
      background: "bg-pink-500",
      isProtected: true,
      group: "Management",
    },
    {
      name: "Defer Payment",
      href: RoutePath.DeferredPayments,
      icon: CalendarArrowDownIcon,
      requiredPackages: [PossiblePackage.DeferPayment],
      description: "",
      background: "bg-blue-500",
      isProtected: true,
      group: "Reports",
    },
    {
      name: "Trader Links",
      href: RoutePath.ManageTraders,
      icon: BuildingIcon,
      requiredPackages: [PossiblePackage.TransactionalReports],
      description: "Manage Traders",
      background: "bg-pink-500",
      isProtected: true,
      group: "Management",
    },
    {
      name: "Manual Upload",
      href: RoutePath.TicketsUpload,
      icon: UploadIcon,
      requiredPackages: [],
      description: "",
      background: "bg-pink-500",
      isProtected: true,
      group: "Tickets",
    },
    {
      name: "Contract Quantity",
      href: RoutePath.ContractQuantityAlertDashboard,
      icon: BellIcon,
      requiredPackages: [PossiblePackage.ContractQuantity],
      description: "",
      background: "bg-pink-500",
      isProtected: true,
      group: "Contracts",
    },
    {
      name: "Freight",
      href: RoutePath.SalesAndPurchaseReports,
      icon: ReceiptIcon,
      requiredPackages: [PossiblePackage.Freight],
      description: "Sales and purchase reports",
      background: "bg-pink-500",
      isProtected: true,
      group: "Reports",
    },
    {
      name: "Freight Dashboard",
      href: RoutePath.FreightDashboard,
      icon: TruckIcon,
      requiredPackages: [PossiblePackage.FreightMobile],
      description: "Inspect freight weight ticket photos",
      background: "bg-orange-500",
      isProtected: true,
      group: "Freight",
    },
    {
      name: "Freight Images",
      href: RoutePath.FreightImagesDashboard,
      icon: TruckIcon,
      requiredPackages: [PossiblePackage.FreightMobile],
      description: "Weight ticket photos from mobile users",
      background: "bg-orange-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Freight",
    },
    {
      name: "COGS",
      href: RoutePath.CogsReports,
      icon: ReceiptIcon,
      requiredPackages: [PossiblePackage.Tickets],
      description:
        "Cost of Goods Sold (COGS) and Sales Transactional analysis reports",
      background: "bg-pink-500",
      isProtected: true,
      group: "Reports",
    },
    {
      name: "Spread Report",
      href: RoutePath.SpreadReport,
      icon: ReceiptIcon,
      requiredPackages: [PossiblePackage.TransactionalReports],
      description: "Spread report",
      background: "bg-pink-500",
      isProtected: true,
      group: "Reports",
    },
    {
      name: "AR Report",
      href: RoutePath.ARReport,
      icon: ReceiptIcon,
      requiredPackages: [PossiblePackage.TransactionalReports],
      description: "AR report",
      background: "bg-pink-500",
      isProtected: true,
      group: "Reports",
    },
    {
      name: "GF Report",
      href: RoutePath.GFReport,
      icon: ReceiptIcon,
      requiredPackages: [PossiblePackage.TransactionalReports],
      description: "GF Charges Report",
      background: "bg-pink-500",
      isProtected: true,
      group: "Month End",
    },
    {
      name: "OTC Prem Accrual",
      href: RoutePath.OtcPrem,
      icon: ReceiptIcon,
      requiredPackages: [PossiblePackage.TransactionalReports],
      description: "OTC Fee Premium Accrual",
      background: "bg-pink-500",
      isProtected: true,
      group: "Month End",
    },
    {
      name: "Contract MTM",
      href: RoutePath.ContractMTM,
      icon: ReceiptIcon,
      requiredPackages: [PossiblePackage.TransactionalReports],
      description: "Contract MTM Report",
      background: "bg-pink-500",
      isProtected: true,
      group: "Reports",
    },
    {
      name: "Marketing Fees Report",
      href: RoutePath.MarketingTransactionalReport,
      icon: FileSpreadsheetIcon,
      requiredPackages: [PossiblePackage.TransactionalReports],
      description: "Marketing Fees",
      background: "bg-pink-500",
      isProtected: true,
      group: "Month End",
    },
    {
      name: "McClusky Fee Accrual",
      href: RoutePath.McCluskyFeeAccrual,
      icon: ReceiptIcon,
      requiredPackages: [PossiblePackage.TransactionalReports],
      description: "McClusky Fee Accrual",
      background: "bg-pink-500",
      isProtected: true,
      group: "Month End",
    },
    {
      name: "Invoice Report",
      href: RoutePath.InvoiceAndStatementReport,
      icon: FileSpreadsheetIcon,
      requiredPackages: [PossiblePackage.InvoiceAndStatement],
      description: "Invoice and Statements",
      background: "bg-pink-500",
      isProtected: true,
      group: "Reports",
    },
    {
      name: "Queued Documents",
      href: RoutePath.QueuedDocuments,
      icon: ListStartIcon,
      requiredPackages: [PossiblePackage.SignatureDocuments],
      description: "View documents queued to send",
      background: "bg-pink-500",
      isProtected: true,
      group: "Contracts",
    },
    {
      name: "ATS",
      href: RoutePath.TradingAtsLive,
      icon: CandlestickChartIcon,
      requiredPackages: [PossiblePackage.AtsRead],
      description: "Submit commands to ATS and view its current position.",
      background: "bg-blue-500",
      isProtected: true,
      group: "Trading",
    },
    {
      name: "Backtest",
      href: RoutePath.TradingBacktest,
      icon: FlaskConicalIcon,
      requiredPackages: [PossiblePackage.AtsBacktest],
      description: "Backtest ATS algorithms",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: false,
      group: "Trading",
    },
    {
      name: "ATS Data",
      href: RoutePath.TradingAtsData,
      icon: BarChartBigIcon,
      requiredPackages: [PossiblePackage.AtsBacktest],
      description: "View and download recent data, such as Price Bars.",
      background: "bg-blue-500",
      isProtected: true,
      group: "Trading",
    },
    {
      name: "Direct Sales",
      href: RoutePath.DirectSales,
      icon: BarChartBigIcon,
      requiredPackages: [PossiblePackage.DirectSale],
      description: "",
      background: "bg-blue-500",
      isProtected: true,
      group: "Contracts",
    },
    {
      name: "Settings",
      href: RoutePath.Settings,
      icon: BarChartBigIcon,
      requiredPackages: [],
      description: "",
      background: "bg-blue-500",
      isProtected: true,
      group: "Contracts",
    },
    {
      name: "Destination Hours",
      href: RoutePath.DestinationHoursDashboard,
      icon: BarChartBigIcon,
      requiredPackages: [PossiblePackage.DestinationHours],
      description: "",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Communication",
    },
    {
      name: "Destination Hours Improved",
      href: RoutePath.DestinationHoursImprovedDashboard,
      icon: BarChartBigIcon,
      requiredPackages: [PossiblePackage.DestinationHours],
      description: "",
      background: "bg-blue-500",
      isProtected: true,
      group: "Communication",
    },
    {
      name: "Grain Samples",
      href: RoutePath.GrainSamplesDashboard,
      icon: GrainIcon,
      requiredPackages: [PossiblePackage.GrainSamples],
      description: "",
      background: "bg-pink-500",
      isProtected: true,
      group: "Tickets",
    },
    {
      name: "Grain Positions",
      href: RoutePath.GrainPositionsDashboard,
      icon: GrainIcon,
      requiredPackages: [PossiblePackage.GrainPosition],
      description: "",
      background: "bg-pink-500",
      isProtected: true,
      group: "Tickets",
    },
    {
      name: "Mobile Resources",
      href: RoutePath.MobileResources,
      icon: FileIcon,
      requiredPackages: [PossiblePackage.ClientManageUsers],
      description: "",
      background: "bg-pink-500",
      isProtected: true,
      group: "Management",
    },
    {
      name: "Risk Management",
      href: RoutePath.RiskManagement,
      icon: UmbrellaIcon,
      requiredPackages: [PossiblePackage.RiskManagementCropProfile],
      description: "",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Risk",
    },
    {
      name: "Risk Cash Sales",
      href: RoutePath.RiskManagementCashSales,
      icon: UmbrellaIcon,
      requiredPackages: [PossiblePackage.RiskManagementCropProfile],
      description: "",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Risk",
    },
    {
      name: "Risk Summary",
      href: RoutePath.RiskManagementSummary,
      icon: UmbrellaIcon,
      requiredPackages: [PossiblePackage.RiskManagementCropProfile],
      description: "",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Risk",
    },
    {
      name: "Risk",
      href: RoutePath.RiskManagementFull,
      icon: UmbrellaIcon,
      requiredPackages: [PossiblePackage.RiskManagementCropProfile],
      description: "",
      background: "bg-blue-500",
      isProtected: true,
      group: "Risk",
    },
    {
      name: "Brokerage",
      href: RoutePath.RiskManagementFullBrokerage,
      icon: UmbrellaIcon,
      requiredPackages: [PossiblePackage.RiskManagementCropProfile],
      description: "",
      background: "bg-blue-500",
      isProtected: true,
      group: "Risk",
    },
    {
      name: "Sales Notes",
      href: RoutePath.RiskManagementSalesNotes,
      icon: UmbrellaIcon,
      requiredPackages: [PossiblePackage.RiskManagementCropProfile],
      description: "",
      background: "bg-blue-500",
      isProtected: true,
      group: "Risk",
    },
    {
      name: "Open Orders",
      href: RoutePath.RiskManagementFullBrokerageOpenOrders,
      icon: UmbrellaIcon,
      requiredPackages: [PossiblePackage.RiskManagementCropProfile],
      description: "",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Risk",
    },
    {
      name: "P&S",
      href: RoutePath.RiskManagementFullBrokeragePAndS,
      icon: UmbrellaIcon,
      requiredPackages: [PossiblePackage.RiskManagementCropProfile],
      description: "",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Risk",
    },
    {
      name: "Recommendations",
      href: RoutePath.RiskManagementRecommendations,
      icon: UmbrellaIcon,
      requiredPackages: [PossiblePackage.RiskManagementCropProfile],
      description: "",
      background: "bg-blue-500",
      isProtected: true,
      group: "Risk",
    },
    {
      name: "Projected Sales",
      href: RoutePath.RiskMarketZone,
      icon: UmbrellaIcon,
      requiredPackages: [PossiblePackage.RiskManagementCropProfile],
      description: "",
      background: "bg-blue-500",
      isProtected: true,
      group: "Risk",
    },

    {
      name: "Risk Crop Profiles",
      href: RoutePath.RiskManagementCropProfiles,
      icon: UmbrellaIcon,
      requiredPackages: [PossiblePackage.RiskManagementCropProfile],
      description: "Cash Sales - Crop Profiles",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Risk",
    },
    {
      name: "Birthday Fund",
      href: RoutePath.BirthdayFund,
      icon: CakeIcon,
      requiredPackages: [PossiblePackage.BirthdayFund],
      description: "Birthday Fund",
      background: "bg-blue-500",
      isProtected: true,
      group: "General",
    },
    {
      name: "Birthday Fund Requests",
      href: RoutePath.BirthdayFundRequests,
      icon: CakeIcon,
      requiredPackages: [PossiblePackage.BirthdayFund],
      description: "",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "General",
    },
    {
      name: "Birthday Fund Certificates",
      href: RoutePath.BirthdayFundCertificates,
      icon: CakeIcon,
      requiredPackages: [PossiblePackage.BirthdayFund],
      description: "",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "General",
    },
    {
      name: "Birthday Fund Certificate List",
      href: RoutePath.BirthdayFundCertificateList,
      icon: CakeIcon,
      requiredPackages: [PossiblePackage.BirthdayFund],
      description: "",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "General",
    },
    {
      name: "Create Birthday Fund Certificate",
      href: RoutePath.BirthdayFundCreateCertificate,
      icon: CakeIcon,
      requiredPackages: [PossiblePackage.BirthdayFund],
      description: "",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "General",
    },
    {
      name: "Create Birthday Fund Instance",
      href: RoutePath.BirthdayFundCreateInstance,
      icon: CakeIcon,
      requiredPackages: [PossiblePackage.BirthdayFund],
      description: "",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "General",
    },
    {
      name: "Beacon Fund",
      href: RoutePath.BeaconFund,
      icon: CakeIcon,
      requiredPackages: [PossiblePackage.BeaconFund],
      description: "Beacon Fund",
      background: "bg-blue-500",
      isProtected: true,
      group: "General",
    },
    {
      name: "Brokerage",
      href: RoutePath.Brokerage,
      icon: FileSpreadsheetIcon,
      requiredPackages: [PossiblePackage.Brokerage],
      description: "Brokerage Statements",
      background: "bg-blue-500",
      isProtected: true,
      group: "Reports",
    },
    {
      name: "Brokerage Files",
      href: RoutePath.BrokerageFiles,
      icon: FileSpreadsheetIcon,
      requiredPackages: [PossiblePackage.Brokerage],
      description: "Brokerage statement files",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Reports",
    },
    {
      name: "Brokerage Report Settings",
      href: RoutePath.BrokerageReportSettings,
      icon: FileSpreadsheetIcon,
      requiredPackages: [PossiblePackage.Brokerage],
      description: "Configure brokerage report settings",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Reports",
    },
    {
      name: "TT Orders",
      href: RoutePath.TTOrders,
      icon: FileSpreadsheetIcon,
      requiredPackages: [PossiblePackage.TtOrders],
      description: "TT Orders",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Trading",
    },
    {
      name: "TT Orders Dashboard",
      href: RoutePath.TTOrdersDashboard,
      icon: FileSpreadsheetIcon,
      requiredPackages: [PossiblePackage.TtOrders],
      description: "TT Orders dashboard",
      background: "bg-blue-500",
      isProtected: true,
      group: "Trading",
    },
    {
      name: "TT Order Detail",
      href: RoutePath.TTOrdersDetail,
      icon: FileSpreadsheetIcon,
      requiredPackages: [PossiblePackage.TtOrders],
      description: "TT order detail",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Trading",
    },
    {
      name: "Export Sales Tracker",
      href: RoutePath.ExportSalesTracker,
      icon: ReceiptIcon,
      requiredPackages: [PossiblePackage.ExportSalesTracker],
      description: "",
      background: "bg-pink-500",
      isProtected: true,
      group: "Reports",
    },
    {
      name: "CFR Documents",
      href: RoutePath.CfrDocuments,
      icon: FileIcon,
      requiredPackages: [PossiblePackage.CfrDocument],
      description: "Create and edit CFR documents with PDF field management",
      background: "bg-blue-500",
      isProtected: true,
      group: "Contracts",
    },
    {
      name: "CFR Reports",
      href: RoutePath.CfrDocumentsReports,
      icon: FileIcon,
      requiredPackages: [PossiblePackage.CfrDocument],
      description: "Review CFR suggestions and bulk send documents",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Contracts",
    },
    {
      name: "CFR Templates",
      href: RoutePath.CfrDocumentsTemplates,
      icon: FileIcon,
      requiredPackages: [PossiblePackage.CfrDocument],
      description: "Manage CFR templates",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Contracts",
    },
    {
      name: "CFR Template Preview",
      href: RoutePath.CfrDocumentsTemplatesView,
      icon: FileIcon,
      requiredPackages: [PossiblePackage.CfrDocument],
      description: "Preview a CFR template PDF",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Contracts",
    },
    {
      name: "Create CFR Template",
      href: RoutePath.CfrDocumentsTemplatesCreate,
      icon: FileIcon,
      requiredPackages: [PossiblePackage.CfrDocument],
      description: "Create a new CFR template",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Contracts",
    },
    {
      name: "CFR Instances",
      href: RoutePath.CfrDocumentsInstances,
      icon: FileIcon,
      requiredPackages: [PossiblePackage.CfrDocument],
      description: "Track CFR sends and signatures",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Contracts",
    },
    {
      name: "CFR Sent Preview",
      href: RoutePath.CfrDocumentsInstanceSentPreview,
      icon: FileIcon,
      requiredPackages: [PossiblePackage.CfrDocument],
      description: "Preview sent CFR document",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Contracts",
    },
    {
      name: "CFR Final Preview",
      href: RoutePath.CfrDocumentsInstanceFinalPreview,
      icon: FileIcon,
      requiredPackages: [PossiblePackage.CfrDocument],
      description: "Preview final CFR document",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Contracts",
    },
    {
      name: "ACH Documents",
      href: RoutePath.AchDocuments,
      icon: FileIcon,
      requiredPackages: [PossiblePackage.AchDocuments],
      description: "Create and edit ACH documents with PDF field management",
      background: "bg-blue-500",
      isProtected: true,
      group: "Contracts",
    },
    {
      name: "ACH Templates",
      href: RoutePath.AchDocumentsTemplates,
      icon: FileIcon,
      requiredPackages: [PossiblePackage.AchDocuments],
      description: "Manage ACH templates",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Contracts",
    },
    {
      name: "Create ACH Template",
      href: RoutePath.AchDocumentsTemplatesCreate,
      icon: FileIcon,
      requiredPackages: [PossiblePackage.AchDocuments],
      description: "Create a new ACH template",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Contracts",
    },
    {
      name: "ACH Template Preview",
      href: RoutePath.AchDocumentsTemplatesView,
      icon: FileIcon,
      requiredPackages: [PossiblePackage.AchDocuments],
      description: "Preview an ACH template PDF",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Contracts",
    },
    {
      name: "ACH Review Instances",
      href: RoutePath.AchDocumentsReviewInstances,
      icon: FileIcon,
      requiredPackages: [PossiblePackage.AchDocuments],
      description: "Review ACH document instances",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Contracts",
    },
    {
      name: "ACH Sent Preview",
      href: RoutePath.AchDocumentsInstanceSentPreview,
      icon: FileIcon,
      requiredPackages: [PossiblePackage.AchDocuments],
      description: "Preview sent ACH document",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Contracts",
    },
    {
      name: "ACH Final Preview",
      href: RoutePath.AchDocumentsInstanceFinalPreview,
      icon: FileIcon,
      requiredPackages: [PossiblePackage.AchDocuments],
      description: "Preview final ACH document",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Contracts",
    },
    {
      name: "Agtech",
      href: RoutePath.Agtech,
      icon: FileIcon,
      requiredPackages: [],
      description: "Preview sent ACH document",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Contracts",
    },
    {
      name: "Agtech Reports",
      href: RoutePath.AgtechReports,
      icon: FileIcon,
      requiredPackages: [PossiblePackage.ContractReport],
      description: "Agtech Info",
      background: "bg-blue-500",
      isProtected: true,
      group: "Contracts",
    },
    {
      name: "Destination Packets",
      href: RoutePath.DestinationPackets,
      icon: FileIcon,
      requiredPackages: [PossiblePackage.DestinationPackets],
      description: "Create and edit destination packet documents with PDF field management",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: false,
      group: "Contracts",
    },
    {
      name: "Destination Packet Templates",
      href: RoutePath.DestinationPacketsTemplates,
      icon: FileIcon,
      requiredPackages: [PossiblePackage.DestinationPackets],
      description: "Manage destination packet templates",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Contracts",
    },
    {
      name: "Create Destination Packet Template",
      href: RoutePath.DestinationPacketsTemplatesCreate,
      icon: FileIcon,
      requiredPackages: [PossiblePackage.DestinationPackets],
      description: "Create a new destination packet template",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Contracts",
    },
    {
      name: "Destination Packet Template View",
      href: RoutePath.DestinationPacketsTemplatesView,
      icon: FileIcon,
      requiredPackages: [PossiblePackage.DestinationPackets],
      description: "View a destination packet template PDF",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Contracts",
    },
    {
      name: "Destination Packet Template Preview",
      href: RoutePath.DestinationPacketsTemplatesPreview,
      icon: FileIcon,
      requiredPackages: [PossiblePackage.DestinationPackets],
      description: "Preview a destination packet template PDF",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Contracts",
    },
    {
      name: "Send Destination Packet Envelope",
      href: RoutePath.DestinationPacketsSendEnvelope,
      icon: FileIcon,
      requiredPackages: [PossiblePackage.DestinationPackets],
      description: "Send a destination packet envelope",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Contracts",
    },
    {
      name: "Destination Packet Instances",
      href: RoutePath.DestinationPacketsInstances,
      icon: FileIcon,
      requiredPackages: [PossiblePackage.DestinationPackets],
      description: "Track destination packet sends and signatures",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Contracts",
    },
    {
      name: "Destination Packet Sent Preview",
      href: RoutePath.DestinationPacketsInstanceSentPreview,
      icon: FileIcon,
      requiredPackages: [PossiblePackage.DestinationPackets],
      description: "Preview sent destination packet document",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Contracts",
    },
    {
      name: "Destination Packet Final Preview",
      href: RoutePath.DestinationPacketsInstanceFinalPreview,
      icon: FileIcon,
      requiredPackages: [PossiblePackage.DestinationPackets],
      description: "Preview final destination packet document",
      background: "bg-blue-500",
      isProtected: true,
      excludeFromDashboard: true,
      group: "Contracts",
    },
  ].filter((item: PageDefinition) => {
    if (!filterByPackages) return true;
    if (!item.requiredPackages.length) return true;

    return doesUserHavePackages({
      requiredPackages: item.requiredPackages,
      member,
    });
  });

  // Find the best match (longest prefix)
  let bestMatchIndex = -1;
  let bestMatchLength = -1;

  if (currentPathname) {
    filteredPages.forEach((page: PageDefinition, index: number) => {
      // Check if currentPathname starts with page.href
      if (currentPathname.startsWith(page.href)) {
        // If it's a prefix match, check if it's the longest one found so far
        // Special case: If href is '/', only match exactly '/'. Avoids matching every path.
        if (page.href === "/" && currentPathname !== "/") {
          return; // Don't match root for non-root paths
        }

        if (page.href.length > bestMatchLength) {
          bestMatchLength = page.href.length;
          bestMatchIndex = index;
        }
      }
    });
  }

  // Add the 'current' property based on the best match
  return filteredPages.map((page: PageDefinition, index: number) => ({
    ...page,
    current: index === bestMatchIndex,
  }));
}
